import { useEffect } from "react";
import { LeafModal } from "../../../common/leaf/LeafModal/LeafModal";
import ServerSideExportDownload from "../ServerSideExportCommon/ServerSideExportDownload/ServerSideExportDownload";
import defaultContent from "../../../content/serverSideExport";
import ssiUplineDefaultContent from "../../../content/ssiUplineReport";
import { useTranslation } from "react-i18next";
import "./SSIUplineExportModal.scss";
import {
  dispatchNotifyInProgressEvent,
  dispatchOpenExportV2ModalEvent,
} from "../ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import {
  exportAnotherMenuTypeTrack,
  exportDeleteTypeTrack,
  exportDownloadTypeTrack,
} from "../../../common/tealium/Export/ServerSideExportTrack";
import { getTranslatedFormats } from "../ServerSideExportCommon/ServerSideExportUtil/ServerSideExportFormatUtil";
import {
  ExportFormat,
  FORMAT_CSV,
  FORMAT_PDF,
  FORMAT_XLSX,
  MODULE_SSI_UPLINE,
  MODULE_SSI_UPLINE_IN_WINDOW,
  MODULE_SSI_UPLINE_PRE_WINDOW,
  MODULE_SSI_UPLINE_OUT_OF_PROGRAM,
  MODULE_SSI_UPLINE_EARNED_PAYMENT,
} from "../../../common/constants/export-constants";
import { isExportDisabledBySessionSsoOrigin, updateInProgressIndicator } from "../../../common/util/exportUtil";
import {
  ExportDetails,
  ExportModuleIdParam,
  SsiUplineExportReqBody,
} from "../../../services/ExportV2/exportV2API.types";
import { useGetExportListV2Query, useSsiUplineExportV2Mutation } from "../../../services/ExportV2/exportV2API";
import { storedIsoCountry } from "../../../common/util/config";
import { storedLocale } from "../../Authentication/util";
import { useAppSelector } from "../../../store";
import { ANA_MARKETS } from "../../../common/constants/market-constants";
import { ssiUplineReportTabs } from "../../../reducers/ssiUplineReport";
import { track } from "../../../common/util/tealium";
import TEALIUM from "../../../common/enums/tealium-enums";

type Props = {
  open: boolean;
  isFetching: boolean;
  onCloseCallbackFnInParent: () => void;
  activeView: string;
};

export const SSIUplineExportModal = ({ open, isFetching, onCloseCallbackFnInParent, activeView }: Props) => {
  const { t } = useTranslation(["serverSideExport", "ssiUplineReport"]);

  const onCloseCallbackFn = () => {
    onCloseCallbackFnInParent();
  };

  const isoCountryCode = storedIsoCountry();
  const locale = storedLocale();
  const user = useAppSelector((state) => state.boot?.user);
  const affAbo = user?.aff && user?.abo ? `${user.aff}-${user.abo}` : "";
  const shellConfig = useAppSelector((state) => state.boot.shellConfig);

  const requirementFields = [
    {
      id: "ssiVcsRequirement",
      title: t("exportFields.vcs", defaultContent.exportFields.vcs).toString(),
      display: ANA_MARKETS.includes(isoCountryCode),
    },
    {
      id: "ssiPpvRequirement",
      title:
        user.aff === "430"
          ? `${t("exportFields.pcpv", defaultContent.exportFields.pcpv)} / ${t(
              "exportFields.vcsPV",
              defaultContent.exportFields.vcsPV,
            )}`
          : t("exportFields.personalPV", defaultContent.exportFields.personalPV).toString(),
      display: true,
    },
  ]
    .filter((field) => field.display)
    .map((field) => ({
      id: field.id,
      title: field.title,
    }));

  const inWindowFields = [
    {
      id: "ssiInPaymentMonth",
      title: t("exportFields.inPaymentMonth", defaultContent.exportFields.inPaymentMonth).toString(),
    },
    {
      id: "aboNo",
      title: t("exportFields.aboNo", defaultContent.exportFields.aboNo).toString(),
    },
    {
      id: "name",
      title: t("exportFields.name", defaultContent.exportFields.name).toString(),
    },
    ...requirementFields,
    {
      id: "ssiProgramStartMonth",
      title: t("exportFields.programStartDate", defaultContent.exportFields.programStartDate).toString(),
    },
    {
      id: "ssiMonthsQualified",
      title: t("exportFields.monthsQualified", defaultContent.exportFields.monthsQualified).toString(),
    },
    {
      id: "ssiPaymentsReceived",
      title: t("exportFields.paymentsReceived", defaultContent.exportFields.paymentsReceived).toString(),
    },
    {
      id: "sponsorABONo",
      title: t("exportFields.sponsorABONo", defaultContent.exportFields.sponsorABONo).toString(),
    },
    {
      id: "sponsorName",
      title: t("exportFields.sponsorName", defaultContent.exportFields.sponsorName).toString(),
    },
    {
      id: "phone",
      title: t("exportFields.phone", defaultContent.exportFields.phone).toString(),
    },
    {
      id: "email",
      title: t("exportFields.email", defaultContent.exportFields.email).toString(),
    },
    {
      id: "address",
      title: t("exportFields.address", defaultContent.exportFields.address).toString(),
    },
  ];

  const preWindowFields = [
    {
      id: "ssiEntryMonth",
      title: t("exportFields.entryMonth", defaultContent.exportFields.entryMonth).toString(),
    },
    {
      id: "aboNo",
      title: t("exportFields.aboNo", defaultContent.exportFields.aboNo).toString(),
    },
    {
      id: "name",
      title: t("exportFields.name", defaultContent.exportFields.name).toString(),
    },
    ...requirementFields,
    {
      id: "ssiPreWindowMonthOne",
      title: t("exportFields.preWindowMonthOne", defaultContent.exportFields.preWindowMonthOne).toString(),
    },
    {
      id: "ssiPreWindowMonthTwo",
      title: t("exportFields.preWindowMonthTwo", defaultContent.exportFields.preWindowMonthTwo).toString(),
    },
    {
      id: "ssiPreWindowMonthThree",
      title: t("exportFields.preWindowMonthThree", defaultContent.exportFields.preWindowMonthThree).toString(),
    },
    {
      id: "sponsorABONo",
      title: t("exportFields.sponsorABONo", defaultContent.exportFields.sponsorABONo).toString(),
    },
    {
      id: "sponsorName",
      title: t("exportFields.sponsorName", defaultContent.exportFields.sponsorName).toString(),
    },
    {
      id: "phone",
      title: t("exportFields.phone", defaultContent.exportFields.phone).toString(),
    },
    {
      id: "email",
      title: t("exportFields.email", defaultContent.exportFields.email).toString(),
    },
    {
      id: "address",
      title: t("exportFields.address", defaultContent.exportFields.address).toString(),
    },
  ];

  const outOfProgramFields = [
    {
      id: "aboNo",
      title: t("exportFields.aboNo", defaultContent.exportFields.aboNo).toString(),
    },
    {
      id: "name",
      title: t("exportFields.name", defaultContent.exportFields.name).toString(),
    },
    ...requirementFields,
    {
      id: "ssiProgramStartMonth",
      title: t("exportFields.programStartDate", defaultContent.exportFields.programStartDate).toString(),
    },
    {
      id: "ssiMonthsInProgram",
      title: t("exportFields.monthsInProgram", defaultContent.exportFields.monthsInProgram).toString(),
    },
    {
      id: "sponsorABONo",
      title: t("exportFields.sponsorABONo", defaultContent.exportFields.sponsorABONo).toString(),
    },
    {
      id: "sponsorName",
      title: t("exportFields.sponsorName", defaultContent.exportFields.sponsorName).toString(),
    },
    {
      id: "phone",
      title: t("exportFields.phone", defaultContent.exportFields.phone).toString(),
    },
    {
      id: "email",
      title: t("exportFields.email", defaultContent.exportFields.email).toString(),
    },
    {
      id: "address",
      title: t("exportFields.address", defaultContent.exportFields.address).toString(),
    },
  ];

  const earnedPaymentFields = [
    {
      id: "aboNo",
      title: t("exportFields.aboNo", defaultContent.exportFields.aboNo).toString(),
    },
    {
      id: "name",
      title: t("exportFields.name", defaultContent.exportFields.name).toString(),
    },
    {
      id: "ssiEapProgramStartDate",
      title: t("exportFields.programStartDate", defaultContent.exportFields.programStartDate).toString(),
    },
    {
      id: "ssiEapPaymentMonth",
      title: t("exportFields.paymentMonth", defaultContent.exportFields.paymentMonth).toString(),
    },
    {
      id: "ssiEapPaymentsReceived",
      title: t("exportFields.paymentsReceived", defaultContent.exportFields.paymentsReceived).toString(),
    },
    {
      id: "sponsorABONo",
      title: t("exportFields.sponsorABONo", defaultContent.exportFields.sponsorABONo).toString(),
    },
    {
      id: "sponsorName",
      title: t("exportFields.sponsorName", defaultContent.exportFields.sponsorName).toString(),
    },
    {
      id: "phone",
      title: t("exportFields.phone", defaultContent.exportFields.phone).toString(),
    },
    {
      id: "email",
      title: t("exportFields.email", defaultContent.exportFields.email).toString(),
    },
    {
      id: "address",
      title: t("exportFields.address", defaultContent.exportFields.address).toString(),
    },
  ];

  const getExportType = (activeView: string) => {
    switch (activeView) {
      case ssiUplineReportTabs.GRACE_PERIOD_TAB:
        return {
          exportType: MODULE_SSI_UPLINE_PRE_WINDOW,
          exportTypeTitle: t("tabs.inGracePeriod.label", ssiUplineDefaultContent.tabs.inGracePeriod.label, {
            ns: "ssiUplineReport",
          }).toString(),
          fields: preWindowFields,
        };
      case ssiUplineReportTabs.IN_SSI_PROGRAM_TAB:
        return {
          exportType: MODULE_SSI_UPLINE_IN_WINDOW,
          exportTypeTitle: t("tabs.inSSIProgram.label", ssiUplineDefaultContent.tabs.inSSIProgram.label, {
            ns: "ssiUplineReport",
          }).toString(),
          fields: inWindowFields,
        };
      case ssiUplineReportTabs.OUT_SSI_PROGRAM_TAB:
        return {
          exportType: MODULE_SSI_UPLINE_OUT_OF_PROGRAM,
          exportTypeTitle: t("tabs.outOfSSIProgram.label", ssiUplineDefaultContent.tabs.outOfSSIProgram.label, {
            ns: "ssiUplineReport",
          }).toString(),
          fields: outOfProgramFields,
        };
      case ssiUplineReportTabs.EARNED_PAYMENT_TAB:
        return {
          exportType: MODULE_SSI_UPLINE_EARNED_PAYMENT,
          exportTypeTitle: t("tabs.earnedPayment.label", ssiUplineDefaultContent.tabs.earnedPayment.label, {
            ns: "ssiUplineReport",
          }).toString(),
          fields: earnedPaymentFields,
        };
      default:
        return {
          exportType: "",
          exportTypeTitle: "",
          fields: [],
        };
    }
  };

  const { selectedPeriod } = useAppSelector((state) => state.period);
  const moduleId = MODULE_SSI_UPLINE;
  const { exportType, fields, exportTypeTitle } = getExportType(activeView);

  const exportPayload = {
    ExportType: exportType,
    ModuleID: moduleId,
    Period: selectedPeriod,
    Locale: locale,
    BusinessNature: "1",
    CountryCode: isoCountryCode,
    ReportAffAbo: affAbo,
    CurrencyCode: shellConfig.isoCurrencyCode || "USD",
    TextOverride: {
      headers: {
        company: t("textOverride.headers.company", defaultContent.textOverride.headers.company).toString(),
        bonusPeriod: t("textOverride.headers.bonusPeriod", defaultContent.textOverride.headers.bonusPeriod).toString(),
        disclaimer: t("textOverride.headers.disclaimer", defaultContent.textOverride.headers.disclaimer).toString(),
      },
      phoneType: {
        primary: t("textOverride.phoneType.primary", defaultContent.textOverride.phoneType.primary).toString(),
        secondary: t("textOverride.phoneType.secondary", defaultContent.textOverride.phoneType.secondary).toString(),
        home: t("textOverride.phoneType.home", defaultContent.textOverride.phoneType.home).toString(),
        alternate: t("textOverride.phoneType.alternate", defaultContent.textOverride.phoneType.alternate).toString(),
        mobile: t("textOverride.phoneType.mobile", defaultContent.textOverride.phoneType.mobile).toString(),
        business: t("textOverride.phoneType.business", defaultContent.textOverride.phoneType.business).toString(),
        businessFax: t(
          "textOverride.phoneType.businessFax",
          defaultContent.textOverride.phoneType.businessFax,
        ).toString(),
      },
      name: {
        confidentialUser: t(
          "textOverride.name.confidentialUser",
          defaultContent.textOverride.name.confidentialUser,
        ).toString(),
        newCustomer: t("textOverride.name.newCustomer", defaultContent.textOverride.name.newCustomer).toString(),
        notAvailable: t("textOverride.name.notAvailable", defaultContent.textOverride.name.notAvailable).toString(),
      },
      ssiUpline: {
        yes: t("textOverride.ssiUpline.yes", defaultContent.textOverride.ssiUpline.yes).toString(),
        no: t("textOverride.ssiUpline.no", defaultContent.textOverride.ssiUpline.no).toString(),
        earned: t("textOverride.ssiUpline.earned", defaultContent.textOverride.ssiUpline.earned).toString(),
        notEarned: t("textOverride.ssiUpline.notEarned", defaultContent.textOverride.ssiUpline.notEarned).toString(),
        potential: t("textOverride.ssiUpline.potential", defaultContent.textOverride.ssiUpline.potential).toString(),
      },
    },
    Fields: fields,
    Format: {
      address: {
        pattern: "{address_lines} | {municipality} | {region} | {postal_code} | {country}",
        addressLineDivider: " | ",
      },
    },
  };
  const [ssiUplineExport] = useSsiUplineExportV2Mutation();

  const onExportAnotherClick = async () => {
    if (exportType !== "") {
      dispatchOpenExportV2ModalEvent("ssiUpline", false);
      onCloseCallbackFnInParent();
      try {
        await ssiUplineExport(exportPayload as SsiUplineExportReqBody);
        dispatchNotifyInProgressEvent();
        exportAnotherMenuTypeTrack("ssiUpline");
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Failed to trigger export for module "${MODULE_SSI_UPLINE}":`, error);
      }
    }
  };

  const useExportListData = (params: ExportModuleIdParam, options: { skip?: boolean } = {}) => {
    const {
      isFetching,
      data: exportListData,
      refetch,
    } = useGetExportListV2Query(params, { ...options, skip: options.skip });

    return {
      isFetching,
      data: exportListData,
      refetch: refetch,
    };
  };

  const supportedFormats: ExportFormat[] = [FORMAT_XLSX, FORMAT_CSV, FORMAT_PDF];
  const { cml: isExportDisabled } = isExportDisabledBySessionSsoOrigin();
  const { data: exportListData, refetch } = useExportListData({ moduleId }, { skip: isExportDisabled });

  updateInProgressIndicator(exportListData?.Exports);

  useEffect(() => {
    if (open && !isExportDisabled) {
      refetch();
    }
  }, [open, refetch, isExportDisabled]);

  const exportModalDownloadTrack = (formats: string) => {
    track(
      TEALIUM.U_TAG_METHOD.LINK,
      "",
      `${exportTypeTitle}_export_ssi_upline_report`,
      "ssi_upline_report",
      "",
      `export_ssi_report_${formats}`,
    );
  };

  const handleDownloadButtonOnClick = (exportDetails: ExportDetails, fileFormat: string, moduleId: string) => {
    const { ExportType = "", FilteredByKPIS = false } = exportDetails;
    exportModalDownloadTrack(fileFormat);
    exportDownloadTypeTrack(ExportType, fileFormat, FilteredByKPIS, moduleId);
  };

  const handleDeleteButtonOnClick = (exportDetails: ExportDetails, moduleId: string) => {
    const { ExportType = "", ExportStatus, FilteredByKPIS = false } = exportDetails;
    exportDeleteTypeTrack(ExportType, ExportStatus, FilteredByKPIS, moduleId);
    refetch();
  };

  const getExportsForSelectedTab = () => {
    return exportListData?.Exports?.filter((exportItem: ExportDetails) => {
      return exportItem.ExportType === exportType;
    });
  };

  return (
    <div className="ssi-export-modal">
      <LeafModal open={open} id={"ssi-export-modal"} onCloseCallbackFn={() => onCloseCallbackFn()}>
        <ServerSideExportDownload
          title={`${t("availableExportModal.header", defaultContent.availableExportModal.header)}`}
          subTitle={t("availableExportModal.subheader", {
            formats: getTranslatedFormats(t, supportedFormats, defaultContent),
            defaultValue: defaultContent.availableExportModal.subheader,
          })}
          exportTypeContent={{
            currentView: exportTypeTitle,
          }}
          exports={getExportsForSelectedTab()}
          moduleId={MODULE_SSI_UPLINE}
          show={open}
          downloadButtonOnClick={handleDownloadButtonOnClick}
          deleteButtonOnClick={handleDeleteButtonOnClick}
          isFetching={isFetching}
          showExportAnotherButton={true}
          exportAnotherButton={{
            exportText: `${t("exportTypeModal.exportButton", defaultContent.exportTypeModal.exportButton)}`,
            exportAnotherText: `${t(
              "availableExportModal.exportAnotherButton",
              defaultContent.availableExportModal.exportAnotherButton,
            )}`,
            onClick: onExportAnotherClick,
          }}
        />
      </LeafModal>
    </div>
  );
};
